
export const SOUND_NAMES = [
  'join',
  'countdown-tick',
  'countdown-bell',
  'heart-lost',
  'victory',
  'defeat',
] as const;

export type SoundName = (typeof SOUND_NAMES)[number];

export interface SfxOptions {
  muted?: boolean;
  createContext?: () => AudioContext;
}


const MASTER_LEVEL = 0.45;
const MUTE_RAMP = 0.03;
const DUCK_ATTACK = 0.04;
const DUCK_RELEASE = 0.25;

const HEARTBEAT_LEVEL = 0.16;
const HEARTBEAT_FADE = 0.4;
const HEARTBEAT_PERIOD = 1.15;
const HEARTBEAT_LOOKAHEAD = 0.6;
const HEARTBEAT_TICK_MS = 200;

const NOISE_SECONDS = 0.5;
const SCHEDULE_LEAD = 0.02;
const MIN_SOURCE_SECONDS = 0.01;


const clamp01 = (value: number): number => (value < 0 ? 0 : value > 1 ? 1 : value);

function envelope(param: AudioParam, at: number, peak: number, attack: number, dur: number): void {
  const end = at + Math.max(dur, attack * 2);
  param.setValueAtTime(0, at);
  param.linearRampToValueAtTime(peak, at + attack);
  param.exponentialRampToValueAtTime(peak * 0.001, end);
  param.setValueAtTime(0, end);
}

class Voice {
  readonly #ctx: AudioContext;
  readonly #live: Set<Voice>;
  readonly #nodes: AudioNode[] = [];
  readonly #sources: AudioScheduledSourceNode[] = [];
  #pending = 0;
  #dead = false;

  constructor(ctx: AudioContext, live: Set<Voice>) {
    this.#ctx = ctx;
    this.#live = live;
    live.add(this);
  }

  gain(initial = 0): GainNode {
    const node = this.#ctx.createGain();
    node.gain.value = initial;
    this.#nodes.push(node);
    return node;
  }

  filter(type: BiquadFilterType, frequency: number, q = 1): BiquadFilterNode {
    const node = this.#ctx.createBiquadFilter();
    node.type = type;
    node.frequency.value = frequency;
    node.Q.value = q;
    this.#nodes.push(node);
    return node;
  }

  osc(type: OscillatorType, frequency: number, detune = 0): OscillatorNode {
    const node = this.#ctx.createOscillator();
    node.type = type;
    node.frequency.value = frequency;
    node.detune.value = detune;
    this.#sources.push(node);
    return node;
  }

  noise(buffer: AudioBuffer): AudioBufferSourceNode {
    const node = this.#ctx.createBufferSource();
    node.buffer = buffer;
    this.#sources.push(node);
    return node;
  }

  start(source: AudioScheduledSourceNode, at: number, until: number): void {
    this.#pending += 1;
    source.onended = () => {
      this.#pending -= 1;
      if (this.#pending > 0 || this.#dead) return;
      this.#release();
    };
    source.start(at);
    source.stop(Math.max(until, at + MIN_SOURCE_SECONDS));
  }

  teardown(): void {
    if (this.#dead) return;
    for (const source of this.#sources) {
      source.onended = null;
      try {
        source.stop();
      } catch {
      }
    }
    this.#release();
  }

  #release(): void {
    this.#dead = true;
    this.#live.delete(this);
    for (const node of this.#nodes) node.disconnect();
    for (const node of this.#sources) node.disconnect();
  }
}


export class Sfx {
  readonly #createContext: () => AudioContext;
  readonly #live = new Set<Voice>();

  #ctx: AudioContext | null = null;
  #master: GainNode | null = null;
  #heartbeatBus: GainNode | null = null;
  #noise: AudioBuffer | null = null;

  #muted: boolean;
  #disposed = false;
  #unavailable = false;

  #heartbeatWanted = false;
  #heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  #nextBeat = 0;

  constructor(opts: SfxOptions = {}) {
    this.#muted = opts.muted ?? false;
    this.#createContext = opts.createContext ?? (() => new AudioContext());
  }

  get muted(): boolean {
    return this.#muted;
  }

  async unlock(): Promise<void> {
    const ctx = this.#ensure();
    if (!ctx) return;
    try {
      if (ctx.state === 'suspended') await ctx.resume();
      this.#noiseBuffer(ctx);
    } catch {
    }
  }

  play(name: SoundName): void {
    if (this.#disposed || this.#muted) return;
    const ctx = this.#ensure();
    const master = this.#master;
    if (!ctx || !master || ctx.state === 'closed') return;

    let voice: Voice | null = null;
    try {
      if (ctx.state === 'suspended') void ctx.resume().catch(() => {});

      const t0 = ctx.currentTime + SCHEDULE_LEAD;
      voice = new Voice(ctx, this.#live);
      switch (name) {
        case 'join':
          this.#join(voice, master, t0);
          break;
        case 'countdown-tick':
          this.#tick(voice, master, t0, this.#noiseBuffer(ctx));
          break;
        case 'countdown-bell':
          this.#bell(voice, master, t0);
          break;
        case 'heart-lost':
          this.#heartLost(voice, master, t0, this.#noiseBuffer(ctx));
          break;
        case 'victory':
          this.#victory(voice, master, t0);
          break;
        case 'defeat':
          this.#defeat(voice, master, t0);
          break;
      }
    } catch {
      try {
        voice?.teardown();
      } catch {
      }
    }
  }

  setHeartbeat(on: boolean): void {
    if (this.#heartbeatWanted === on) return;
    this.#heartbeatWanted = on;
    this.#syncHeartbeat();
  }

  duck(level: number, ms: number): void {
    if (this.#disposed || this.#muted) return;
    const ctx = this.#ensure();
    const master = this.#master;
    if (!ctx || !master || ctx.state === 'closed') return;

    try {
      const now = ctx.currentTime;
      const hold = Math.max(0, ms) / 1000;
      const target = MASTER_LEVEL * clamp01(level);
      const param = master.gain;
      param.cancelScheduledValues(now);
      param.setValueAtTime(param.value, now);
      param.linearRampToValueAtTime(target, now + DUCK_ATTACK);
      param.setValueAtTime(target, now + DUCK_ATTACK + hold);
      param.linearRampToValueAtTime(this.#level(), now + DUCK_ATTACK + hold + DUCK_RELEASE);
    } catch {
    }
  }

  setMuted(muted: boolean): void {
    if (this.#muted === muted) return;
    this.#muted = muted;
    const ctx = this.#ctx;
    const master = this.#master;
    if (ctx && master && ctx.state !== 'closed') {
      try {
        const now = ctx.currentTime;
        const param = master.gain;
        param.cancelScheduledValues(now);
        param.setValueAtTime(param.value, now);
        param.linearRampToValueAtTime(this.#level(), now + MUTE_RAMP);
      } catch {
      }
    }
    this.#syncHeartbeat();
  }

  dispose(): void {
    if (this.#disposed) return;
    this.#disposed = true;
    this.#heartbeatWanted = false;
    this.#stopScheduler();

    for (const voice of [...this.#live]) {
      try {
        voice.teardown();
      } catch {
      }
    }
    this.#live.clear();

    const ctx = this.#ctx;
    try {
      this.#heartbeatBus?.disconnect();
      this.#master?.disconnect();
    } catch {
    }
    this.#heartbeatBus = null;
    this.#master = null;
    this.#noise = null;
    this.#ctx = null;
    if (ctx && ctx.state !== 'closed') void ctx.close().catch(() => {});
  }


  #level(): number {
    return this.#muted ? 0 : MASTER_LEVEL;
  }

  #ensure(): AudioContext | null {
    if (this.#disposed || this.#unavailable) return null;
    const existing = this.#ctx;
    if (existing) return existing;

    try {
      const ctx = this.#createContext();
      const master = ctx.createGain();
      master.gain.value = this.#level();
      master.connect(ctx.destination);
      const heartbeat = ctx.createGain();
      heartbeat.gain.value = 0;
      heartbeat.connect(master);

      this.#ctx = ctx;
      this.#master = master;
      this.#heartbeatBus = heartbeat;
      return ctx;
    } catch {
      this.#unavailable = true;
      return null;
    }
  }

  #noiseBuffer(ctx: AudioContext): AudioBuffer | null {
    const cached = this.#noise;
    if (cached) return cached;
    try {
      const rate = ctx.sampleRate;
      const length = Math.max(1, Math.floor(rate * NOISE_SECONDS));
      const buffer = ctx.createBuffer(1, length, rate);
      const data = buffer.getChannelData(0);
      for (let i = 0; i < length; i += 1) data[i] = Math.random() * 2 - 1;
      this.#noise = buffer;
      return buffer;
    } catch {
      return null;
    }
  }


  #syncHeartbeat(): void {
    const wanted = this.#heartbeatWanted && !this.#muted && !this.#disposed;
    if (!wanted) {
      this.#stopScheduler();
      const ctx = this.#ctx;
      const bus = this.#heartbeatBus;
      if (!ctx || !bus || ctx.state === 'closed') return;
      try {
        const now = ctx.currentTime;
        bus.gain.cancelScheduledValues(now);
        bus.gain.setValueAtTime(bus.gain.value, now);
        bus.gain.linearRampToValueAtTime(0, now + HEARTBEAT_FADE);
      } catch {
      }
      return;
    }

    const ctx = this.#ensure();
    const bus = this.#heartbeatBus;
    if (!ctx || !bus || ctx.state === 'closed') return;
    try {
      if (ctx.state === 'suspended') void ctx.resume().catch(() => {});
      const now = ctx.currentTime;
      bus.gain.cancelScheduledValues(now);
      bus.gain.setValueAtTime(bus.gain.value, now);
      bus.gain.linearRampToValueAtTime(HEARTBEAT_LEVEL, now + HEARTBEAT_FADE);
      if (this.#heartbeatTimer === null) {
        this.#nextBeat = now + SCHEDULE_LEAD;
        this.#pump();
        this.#heartbeatTimer = setInterval(() => this.#pump(), HEARTBEAT_TICK_MS);
      }
    } catch {
    }
  }

  #stopScheduler(): void {
    if (this.#heartbeatTimer === null) return;
    clearInterval(this.#heartbeatTimer);
    this.#heartbeatTimer = null;
  }

  #pump(): void {
    const ctx = this.#ctx;
    const bus = this.#heartbeatBus;
    if (!ctx || !bus) return;
    if (ctx.state === 'closed') {
      this.#stopScheduler();
      return;
    }
    try {
      const now = ctx.currentTime;
      if (this.#nextBeat < now) this.#nextBeat = now + SCHEDULE_LEAD;
      const horizon = now + HEARTBEAT_LOOKAHEAD;
      while (this.#nextBeat < horizon) {
        this.#thump(bus, this.#nextBeat);
        this.#nextBeat += HEARTBEAT_PERIOD;
      }
    } catch {
    }
  }

  #thump(bus: AudioNode, at: number): void {
    const ctx = this.#ctx;
    if (!ctx) return;
    const voice = new Voice(ctx, this.#live);
    const hits: ReadonlyArray<readonly [offset: number, level: number]> = [
      [0, 1],
      [0.21, 0.62],
    ];
    for (const [offset, level] of hits) {
      const start = at + offset;
      const dur = 0.19;
      const body = voice.gain(0);
      body.connect(bus);
      envelope(body.gain, start, level, 0.006, dur);
      const lp = voice.filter('lowpass', 150, 0.8);
      lp.connect(body);
      const osc = voice.osc('sine', 66);
      osc.frequency.setValueAtTime(66, start);
      osc.frequency.exponentialRampToValueAtTime(32, start + 0.12);
      osc.connect(lp);
      voice.start(osc, start, start + dur);
    }
  }

  #duckHeartbeat(at: number, seconds: number): void {
    const bus = this.#heartbeatBus;
    if (!bus || this.#heartbeatTimer === null) return;
    try {
      const quiet = HEARTBEAT_LEVEL * 0.2;
      const param = bus.gain;
      param.cancelScheduledValues(at);
      param.setValueAtTime(param.value, at);
      param.linearRampToValueAtTime(quiet, at + 0.05);
      param.setValueAtTime(quiet, at + seconds);
      param.linearRampToValueAtTime(HEARTBEAT_LEVEL, at + seconds + HEARTBEAT_FADE);
    } catch {
    }
  }


  #join(v: Voice, bus: AudioNode, t0: number): void {
    const notes: ReadonlyArray<readonly [freq: number, at: number, dur: number]> = [
      [880, t0, 0.6],
      [1318.51, t0 + 0.13, 1.0],
    ];
    for (const [freq, at, dur] of notes) {
      const body = v.gain(0);
      body.connect(bus);
      envelope(body.gain, at, 0.22, 0.005, dur);
      const osc = v.osc('triangle', freq);
      osc.connect(body);
      v.start(osc, at, at + dur);

      const shimmer = v.gain(0);
      shimmer.connect(bus);
      envelope(shimmer.gain, at, 0.06, 0.003, dur * 0.55);
      const partial = v.osc('sine', freq * 2.76);
      partial.connect(shimmer);
      v.start(partial, at, at + dur * 0.55);
    }
  }

  #tick(v: Voice, bus: AudioNode, t0: number, noise: AudioBuffer | null): void {
    if (noise) {
      const click = v.gain(0);
      click.connect(bus);
      envelope(click.gain, t0, 0.3, 0.001, 0.04);
      const band = v.filter('bandpass', 2600, 1.2);
      band.connect(click);
      const source = v.noise(noise);
      source.connect(band);
      v.start(source, t0, t0 + 0.04);
    }

    const tone = v.gain(0);
    tone.connect(bus);
    envelope(tone.gain, t0, 0.16, 0.001, 0.05);
    const osc = v.osc('sine', 1300);
    osc.frequency.setValueAtTime(1300, t0);
    osc.frequency.exponentialRampToValueAtTime(820, t0 + 0.05);
    osc.connect(tone);
    v.start(osc, t0, t0 + 0.05);
  }

  #bell(v: Voice, bus: AudioNode, t0: number): void {
    const f0 = 1046.5;
    const partials: ReadonlyArray<readonly [ratio: number, level: number, dur: number]> = [
      [1, 0.24, 1.8],
      [2.0, 0.1, 1.2],
      [2.76, 0.07, 0.9],
      [5.4, 0.03, 0.5],
    ];
    for (const [ratio, level, dur] of partials) {
      const body = v.gain(0);
      body.connect(bus);
      envelope(body.gain, t0, level, 0.004, dur);
      const osc = v.osc('sine', f0 * ratio);
      osc.connect(body);
      v.start(osc, t0, t0 + dur);
    }
  }

  #heartLost(v: Voice, bus: AudioNode, t0: number, noise: AudioBuffer | null): void {
    const scratchEnd = t0 + 0.26;
    if (noise) {
      const body = v.gain(0);
      body.connect(bus);
      envelope(body.gain, t0, 0.55, 0.004, 0.26);
      const band = v.filter('bandpass', 1800, 6);
      band.connect(body);
      band.frequency.setValueAtTime(1800, t0);
      band.frequency.linearRampToValueAtTime(700, t0 + 0.09);
      band.frequency.linearRampToValueAtTime(2400, t0 + 0.17);
      band.frequency.linearRampToValueAtTime(600, scratchEnd);

      const source = v.noise(noise);
      source.connect(band);
      const rate = source.playbackRate;
      rate.setValueAtTime(1.9, t0);
      rate.linearRampToValueAtTime(0.35, t0 + 0.09);
      rate.linearRampToValueAtTime(1.6, t0 + 0.17);
      rate.linearRampToValueAtTime(0.4, scratchEnd);
      v.start(source, t0, scratchEnd);
    }

    const wahStart = t0 + 0.2;
    const notes: ReadonlyArray<readonly [freq: number, offset: number, dur: number]> = [
      [220, 0, 0.34],
      [184.997, 0.26, 0.34],
      [146.832, 0.52, 0.7],
    ];
    for (const [freq, offset, dur] of notes) {
      const at = wahStart + offset;
      const body = v.gain(0);
      body.connect(bus);
      envelope(body.gain, at, 0.2, 0.02, dur);
      const lp = v.filter('lowpass', 380, 6);
      lp.connect(body);
      lp.frequency.setValueAtTime(380, at);
      lp.frequency.linearRampToValueAtTime(1700, at + dur * 0.3);
      lp.frequency.linearRampToValueAtTime(260, at + dur);
      for (const cents of [-9, 9]) {
        const osc = v.osc('sawtooth', freq, cents);
        osc.frequency.setValueAtTime(freq, at);
        osc.frequency.exponentialRampToValueAtTime(freq * 0.94, at + dur);
        osc.connect(lp);
        v.start(osc, at, at + dur);
      }
    }

    this.#duckHeartbeat(t0, 1.4);
  }

  #victory(v: Voice, bus: AudioNode, t0: number): void {
    const arp = [523.25, 659.25, 783.99] as const;
    for (const [i, freq] of arp.entries()) {
      this.#brass(v, bus, freq, t0 + i * 0.11, 0.2, 0.16, [0]);
    }
    const chord = [523.25, 659.25, 783.99, 1046.5] as const;
    for (const freq of chord) {
      this.#brass(v, bus, freq, t0 + 0.33, 1.1, 0.12, [-5, 6]);
    }
  }

  #brass(
    v: Voice,
    bus: AudioNode,
    freq: number,
    at: number,
    dur: number,
    level: number,
    detunes: readonly number[],
  ): void {
    const body = v.gain(0);
    body.connect(bus);
    envelope(body.gain, at, level, 0.015, dur);
    const lp = v.filter('lowpass', 700, 1);
    lp.connect(body);
    lp.frequency.setValueAtTime(700, at);
    lp.frequency.linearRampToValueAtTime(3200, at + 0.06);
    lp.frequency.linearRampToValueAtTime(1400, at + dur);
    for (const cents of detunes) {
      const osc = v.osc('sawtooth', freq, cents);
      osc.connect(lp);
      v.start(osc, at, at + dur);
    }
  }

  #defeat(v: Voice, bus: AudioNode, t0: number): void {
    const notes: ReadonlyArray<readonly [freq: number, offset: number, dur: number]> = [
      [233.08, 0, 0.3],
      [220.0, 0.3, 0.3],
      [207.65, 0.6, 0.3],
      [196.0, 0.9, 1.2],
    ];
    for (const [freq, offset, dur] of notes) {
      const at = t0 + offset;
      const isLast = dur > 0.5;
      const body = v.gain(0);
      body.connect(bus);
      envelope(body.gain, at, 0.24, 0.04, dur);
      const lp = v.filter('lowpass', 1500, 0.9);
      lp.connect(body);
      lp.frequency.setValueAtTime(1500, at);
      lp.frequency.linearRampToValueAtTime(600, at + dur);

      const osc = v.osc('sawtooth', freq);
      osc.frequency.setValueAtTime(freq, at);
      osc.frequency.exponentialRampToValueAtTime(freq * (isLast ? 0.84 : 0.97), at + dur);
      osc.connect(lp);
      v.start(osc, at, at + dur);

      if (!isLast) continue;
      const lfo = v.osc('sine', 5.5);
      const depth = v.gain(0);
      depth.gain.setValueAtTime(0, at);
      depth.gain.linearRampToValueAtTime(22, at + dur * 0.6);
      depth.gain.linearRampToValueAtTime(6, at + dur);
      lfo.connect(depth);
      depth.connect(osc.detune);
      v.start(lfo, at, at + dur);
    }
  }
}
